import { useEffect, useState, type FormEvent } from 'react';
import type { AccountUser } from './AccountModal';

interface ResetPasswordModalProps {
  open: boolean;
  user: AccountUser | null;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}

export default function ResetPasswordModal({ open, user, onClose, onSubmit }: ResetPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword('');
      setConfirm('');
      setErr('');
      setBusy(false);
      setShowPw(false);
      setShowConfirm(false);
    }
  }, [open]);

  if (!open || !user) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setErr('');
    if (password.length < 6) {
      setErr('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await onSubmit(password);
    } catch (e: any) {
      setErr(e?.message || 'Failed to reset password.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-lg border border-slate-200 w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-base">Reset password</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Set a new password for <span className="font-medium text-slate-700">{user.username}</span>.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">New password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErr('');
                }}
                placeholder="At least 6 characters"
                className="w-full text-sm px-3 py-2 pr-10 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer w-7 h-7 flex items-center justify-center"
                aria-label={showPw ? 'Hide password' : 'Show password'}
              >
                <i className={`${showPw ? 'ri-eye-off-line' : 'ri-eye-line'} w-4 h-4 flex items-center justify-center`} />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setErr('');
                }}
                placeholder="Repeat the new password"
                className="w-full text-sm px-3 py-2 pr-10 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer w-7 h-7 flex items-center justify-center"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              >
                <i className={`${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} w-4 h-4 flex items-center justify-center`} />
              </button>
            </div>
          </div>

          {err && (
            <div className="p-3 rounded-md text-sm bg-red-50 border border-red-200 text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={busy}
              className="flex-1 px-4 py-2 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 cursor-pointer whitespace-nowrap disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <i className="ri-key-2-line w-4 h-4 flex items-center justify-center" /> Reset password
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}